import * as React from "react";

import AddIcon from "@mui/icons-material/Add";
import TextSnippetIcon from "@mui/icons-material/TextSnippet";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import type { Theme } from "@mui/material/styles";
import { TreeItem } from "@mui/x-tree-view/TreeItem";

import { getScribeModalNestedDialogZIndex } from "features/medical-scribe/medical-scribe-constants";

import { TreeNode } from "./text-templates-v2.utils";

// The templates dialog can be raised to the scribe modal's nested-dialog layer, above MUI's
// tooltip layer.
const TOOLTIP_POPPER_PROPS = {
  sx: {
    zIndex: (theme: Theme) =>
      getScribeModalNestedDialogZIndex(theme.zIndex.snackbar) + 1
  }
};

const TEAL = "#0F6E56";
const TEAL_FILL = "#E1F5EE";
const TEAL_BORDER = "#9FE1CB";

// Shallow comparison helper for nodes
const areNodesEqual = (
  prevNodes: TreeNode[],
  nextNodes: TreeNode[]
): boolean => {
  if (prevNodes.length !== nextNodes.length) return false;

  return prevNodes.every((node, index) => {
    const nextNode = nextNodes[index];
    return (
      node.id === nextNode.id &&
      node.title === nextNode.title &&
      node.selector === nextNode.selector &&
      node.isCategory === nextNode.isCategory &&
      areNodesEqual(node.children, nextNode.children)
    );
  });
};

type TextTemplatesTreeItemsProps = {
  nodes: TreeNode[];
  categoriesOnly?: boolean;
  composeTemplateEnabled?: boolean;
  onAddToComposedTemplate?: (templateId: number | string) => void;
};

const TextTemplatesTreeItems = React.memo(
  function TextTemplatesTreeItems({
    nodes,
    categoriesOnly = false,
    composeTemplateEnabled,
    onAddToComposedTemplate
  }: TextTemplatesTreeItemsProps) {
    return (
      <>
        {nodes.map((node) => (
          <TreeItem
            key={node.id}
            nodeId={String(node.selector)}
            label={
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                width="100%"
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight:
                      node.isCategory && node.children.length ? 700 : 400,
                    py: composeTemplateEnabled ? 1.0 : 0.5
                  }}
                >
                  {node.title}
                </Typography>

                {!node.isCategory &&
                  composeTemplateEnabled &&
                  onAddToComposedTemplate && (
                    <Tooltip
                      title="Add to composed template"
                      disableInteractive
                      PopperProps={TOOLTIP_POPPER_PROPS}
                    >
                      <Button
                        size="small"
                        variant="outlined"
                        sx={{
                          height: "28px",
                          width: "16px",
                          minWidth: "16px",
                          maxWidth: "16px",
                          padding: 0,
                          color: TEAL,
                          borderColor: TEAL_BORDER,
                          "&:hover": {
                            borderColor: TEAL,
                            backgroundColor: TEAL_FILL
                          }
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          onAddToComposedTemplate(node.id);
                        }}
                      >
                        <AddIcon />
                      </Button>
                    </Tooltip>
                  )}
              </Stack>
            }
            endIcon={
              !categoriesOnly && !node.isCategory ? (
                <TextSnippetIcon sx={{ opacity: 0.35 }} />
              ) : undefined
            }
          >
            {node.children.length ? (
              <TextTemplatesTreeItems
                nodes={node.children}
                categoriesOnly={categoriesOnly}
                composeTemplateEnabled={composeTemplateEnabled}
                onAddToComposedTemplate={onAddToComposedTemplate}
              />
            ) : undefined}
          </TreeItem>
        ))}
      </>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison to optimize re-renders
    return (
      prevProps.categoriesOnly === nextProps.categoriesOnly &&
      prevProps.composeTemplateEnabled === nextProps.composeTemplateEnabled &&
      prevProps.onAddToComposedTemplate === nextProps.onAddToComposedTemplate &&
      areNodesEqual(prevProps.nodes, nextProps.nodes)
    );
  }
);

export default TextTemplatesTreeItems;
